/*
 * <sss-brand-slideshow> — brand-name tabs, one large photo per brand.
 * Tabs pattern: roving tabindex, aria-selected, Left/Right/Home/End.
 * Autoplay is a CSS animation on the active tab's progress line; when it ends
 * we advance. Pausing pauses that animation, so line and timer never drift.
 * No autoplay for prefers-reduced-motion, in the theme editor, while hovered,
 * focused, off-screen or in a background tab, or after the visitor pauses.
 */
if (!customElements.get('sss-brand-slideshow')) {
  class SssBrandSlideshow extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.list = this.querySelector('[role="tablist"]');
      this.toggle = this.querySelector('[data-toggle]');
      if (!this.tabs.length) return;

      this.index = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true' && this.tabs.length > 1 && !window.Shopify?.designMode;
      this.dataset.autoplay = String(this.autoplay);
      this.state = { userPaused: false, hovered: false, focused: false, visible: false };
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.select(i), opts));
      this.list.addEventListener('keydown', (e) => this.onKeydown(e), opts);
      this.addEventListener('animationend', (e) => {
        if (e.animationName === 'sss-brandshow-progress') this.select(this.index + 1);
      }, opts);
      this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.set({ hovered: true }), opts);
      this.addEventListener('pointerleave', () => this.set({ hovered: false }), opts);
      this.addEventListener('focusin', (e) => this.set({ focused: e.target.matches(':focus-visible') }), opts);
      this.addEventListener('focusout', (e) => this.contains(e.relatedTarget) || this.set({ focused: false }), opts);
      this.toggle?.addEventListener('click', () => this.set({ userPaused: !this.state.userPaused }), opts);
      document.addEventListener('visibilitychange', () => this.update(), opts);
      this.motion.addEventListener?.('change', () => this.update(), opts);

      /* swipe on the photo (touch) — vertical scrolling stays with the page (touch-action: pan-y) */
      const stage = this.querySelector('.sss-brandshow__stage');
      stage?.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') this.startX = e.clientX; }, opts);
      stage?.addEventListener('pointerup', (e) => {
        if (this.startX == null) return;
        const dx = e.clientX - this.startX;
        this.startX = null;
        if (Math.abs(dx) > 40) this.select(this.index + (dx < 0 ? 1 : -1));
      }, opts);
      stage?.addEventListener('pointercancel', () => { this.startX = null; }, opts);

      /* theme editor: selecting a brand block shows its slide */
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.tabs.indexOf(e.target.closest?.('[role="tab"]'));
        if (i > -1) this.select(i);
      }, opts);

      this.observer = new IntersectionObserver(([entry]) => this.set({ visible: entry.isIntersecting }), { threshold: 0.35 });
      this.observer.observe(this);
      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }

    set(patch) {
      Object.assign(this.state, patch);
      this.update();
    }

    update() {
      const { userPaused, hovered, focused, visible } = this.state;
      const reduced = this.motion.matches;
      const running = this.autoplay && !reduced && !userPaused && !hovered && !focused && visible && !document.hidden;
      this.classList.toggle('is-running', running);
      if (this.toggle) {
        const paused = userPaused || reduced;
        this.toggle.setAttribute('aria-pressed', String(paused));
        this.toggle.setAttribute('aria-label', paused ? 'Play brand slideshow' : 'Pause brand slideshow');
      }
    }

    select(i) {
      const n = this.tabs.length;
      const next = ((i % n) + n) % n;
      if (next === this.index) return;
      this.tabs.forEach((tab, k) => {
        const on = k === next;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel, k) => {
        const on = k === next;
        panel.classList.toggle('is-active', on);
        panel.inert = !on;
        if (on) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      });
      this.index = next;
      this.reveal(this.tabs[next]);
    }

    /* keep the active brand visible in the scrolling (phone) list without moving the page */
    reveal(tab) {
      const list = this.list;
      if (list.scrollWidth <= list.clientWidth) return;
      const left = tab.offsetLeft - list.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
      list.scrollTo({ left, behavior: this.motion.matches ? 'auto' : 'smooth' });
    }

    onKeydown(e) {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      let target;
      if (step) target = this.index + step;
      else if (e.key === 'Home') target = 0;
      else if (e.key === 'End') target = this.tabs.length - 1;
      else return;
      e.preventDefault();
      this.select(target);
      this.tabs[this.index].focus();
    }
  }

  customElements.define('sss-brand-slideshow', SssBrandSlideshow);
}
